import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, FormAPI, LegendsAPI, PanelAPI, ToastNotificationAPI, Token } from '@philips-emr/tws-components';
import { Type } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AccreditationEquipmentsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';
import './accreditation.css';
import { ActionTypes } from './update-accreditation-type';

interface Equipment {
  nrSequence: number,
  ieEquipment: string,
  dsEquipment: string,
  ieEquipmentAvailableSUS: string | boolean,
  qtEquipment: number,
  qtEquipmentSUS: number
  editable: string,
  ieAction: ActionTypes,
  dsAction: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

const localValidatorSUS = 'islowerOrEqualTheOtherValue';
@Controller({ fragment: FragmentNames.ACCREDITATION_EQUIPMENTS })
class AccreditationEquipmentsController extends BaseController {
  @Inject(Aliases.FETCH_EQUIPMENTS_LIST)
  private fetchList: FetchAPI;
  @Inject(Aliases.FIELD_AMOUNT_EQUIPMENT_USE)
  private fieldAmountUse: FieldAPI;
  @Inject(Aliases.PANEL_EQUIPMENTS_LIST)
  private panelEquipmentsList: PanelAPI;
  @Inject(Aliases.FORM_EQUIPMENT)
  private formEquipment!: FormAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  private requiredFields: FieldAPI[] = [];

  @OnAfterFetch(Aliases.FETCH_EQUIPMENTS_LIST)
  onAfterFetchEquipmentsList(event: FetchEvent): void {
    const { data } = event;
    if (isEmptyArrayOrObject(data)) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnMount([
    Aliases.FIELD_EQUIPMENT,
    Aliases.FIELD_TYPE_EQUIPMENT,
    Aliases.FIELD_AMOUNT_EQUIPMENT,
    Aliases.FIELD_AMOUNT_EQUIPMENT_USE,
  ])
  onMountRequiredFields(event: MountEvent): void {
    this.requiredFields.push(event.target as FieldAPI);
  }

  @OnMount(Aliases.LAYOUT_GENERAL_INFORMATION)
  onRootLayoutMount(): void {
    const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqAccreditation, nrSeqTypeAccreditation });
    this.fetchList.forceFetch();
  }

  @OnMount(Aliases.PANEL_FORM_EQUIPMENT)
  onMountPanelForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.EQUIPMENTS);
  }

  @HPMSOnCreateMenu(Aliases.CARD_EQUIPMENT)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Equipment>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @OnMount(Aliases.FORM_EQUIPMENT)
  onMountForm(event: MountEvent): void {
    const apiForm = event.target as FormAPI;
    apiForm.setRecord({ ieEquipmentAvailableSUS: false });

    this.fieldAmountUse.addLocalValidator(localValidatorSUS, (amountUse) => {
      const quantity = this.formEquipment.getRecord()['qtEquipment'];
      const valid = !quantity || (Number(amountUse ?? 0) <= Number(quantity ?? 0));

      return [
        {
          message: !valid ? 'provider.valid-amount-use' : '',
          name: localValidatorSUS,
          valid,
        },
      ];
    });
  }

  @HPMSOnOptionClick(Aliases.CARD_EQUIPMENT)
  onOptionClick(event: HpmsCustomCardEvent<Equipment>): void {
    const { data, key } = event;
    const { nrSequence } = data;
    const action = {
      [OptionKey.EDIT]: () => this.editRecord(nrSequence),
      [OptionKey.DELETE]: () => this.deleteRecord(nrSequence),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private editRecord(nrSequence: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.get<Equipment>(Endpoint.GET.EQUIPMENTS, { nrSeqProvider, nrSeqAccreditation, nrSequence })
      .then((resp) => {
        const { body = {} } = resp;
        this.formEquipment.setData(body);
        this.formEquipment.setRecord(body);
        this.formEquipment.validate();
      }).catch((response) => ToastService.open(response));
  }

  private async deleteRecord(nrSequence: number): Promise<void> {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    await HttpService.delete<Equipment>(Endpoint.DELETE.EQUIPMENTS, { nrSeqProvider, nrSeqAccreditation, nrSequence })
      .then(() => {
        this.formEquipment.setRecord({});
        this.fetchList.forceFetch();
      }).catch((response) => ToastService.open(response));
  }

  @OnDynamicContentRender(Aliases.DYNAMIC_FIELD_AVAILABLE_SUS)
  public onDynamicContentRenderField(event: RenderEvent): void {
    const { data, render } = event;
    const { ieEquipmentAvailableSUS } = data as Equipment;
    if (ieEquipmentAvailableSUS === 'S') {
      render((): ReactElement => this.getToken());
    }
  }

  private getToken(): ReactElement {
    return <div className="u-token-list-card">
      <Token value="provider.equipment-available-sus" type={Type.NOTICE} />
    </div>;
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.EQUIPMENTS);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  @OnCancel(Aliases.FORM_EQUIPMENT)
  onCancelForm(): void {
    this.formEquipment.setRecord({});
    this.showValidations(false);
  }

  @OnSubmit(Aliases.FORM_EQUIPMENT)
  onSubmitForm(event: SubmitEvent): void {
    const record = event.record as Equipment;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.post(Endpoint.POST.EQUIPMENTS, { nrSeqProvider, nrSeqAccreditation }, { body: { ...record } })
      .then(() => {
        this.showValidations(false);
        this.formEquipment.setRecord({});
        this.panelEquipmentsList.setVisible(true);
        this.fetchList.forceFetch();
      }).catch((response) => ToastService.open(response));
  }

  private showValidations(validate: boolean): void {
    this.requiredFields.forEach(field => field.setState({ showValidations: validate }));
  }
}

export {
  AccreditationEquipmentsController,
};
